import type { Platform } from '@sparklayer-io/spark-dash-ui/types/platform';
import { getShopifyAppSlug, getWixAppId } from '../constants/environment';
import { isShopify } from '../constants/platform';
import { SHOPIFY_ADMIN_FALLBACK_URL } from '../constants/shopify';

/**
 * Every deep link into a merchant's own platform admin or storefront.
 *
 * This is the single source of truth for these URL shapes, which were previously duplicated in
 * more than one place — that's how they drifted (e.g. Shopify order links existing twice with
 * different platform guards). Anything building a `https://…` pointing at
 * a merchant's store belongs here.
 *
 * `storeId` is whatever `env.store_id` holds for the connected platform: the `.myshopify.com` domain
 * for Shopify, the site ID for Wix, the store hash for BigCommerce.
 */

/**
 * An identifier that may not have loaded or may be absent on the record. Accepted by the
 * cross-platform builders so callers can pass API fields straight through without pre-checking —
 * a missing part yields `null` rather than a URL with `undefined` baked into it.
 */
type MaybeId = string | null | undefined;

// --- Shopify -------

/**
 * A page in the merchant's Shopify admin. `path` is relative to `/admin`
 * (e.g. `'draft_orders'`, `'settings/general'`, `` `customers/${id}` ``).
 */
export function shopifyAdminUrl(storeId: string, path = ''): string {
  const base = `https://${storeId}/admin`;
  return path ? `${base}/${path}` : base;
}

/** The SparkLayer app's page inside the merchant's Shopify admin. */
export function shopifyAppUrl(storeId: string, path = ''): string {
  return shopifyAdminUrl(
    storeId,
    path ? `apps/${getShopifyAppSlug()}/${path}` : `apps/${getShopifyAppSlug()}`,
  );
}

/** The app's setup screen, falling back to the Shopify admin home when the store is unknown. */
export function shopifyAppSetupUrl(storeId?: MaybeId): string {
  return storeId ? shopifyAppUrl(storeId, 'setup') : SHOPIFY_ADMIN_FALLBACK_URL;
}

/** The app's theme-setup screen. Null without a store, since there is nothing to link at. */
export function shopifyThemeSetupUrl(storeId?: MaybeId): string | null {
  return storeId ? shopifyAppUrl(storeId, 'theme-setup') : null;
}

/**
 * The customer list filtered to a SparkLayer group tag.
 *
 * The quotes and spaces around the tag are pre-encoded rather than run through
 * `encodeURIComponent`, which leaves `'` untouched.
 */
export function shopifyCustomersByTagUrl(storeId: string, tag: string): string {
  const segment = `customer_tags%20CONTAINS%20%27${encodeURIComponent(tag)}%27%20`;
  return shopifyAdminUrl(storeId, `customers?segment_query=${segment}`);
}

/**
 * The store's handle without the `.myshopify.com` suffix, for display and for the store-ID field.
 */
export function shopifyStoreHandle(storeId: string): string {
  return storeId.replace('.myshopify.com', '');
}

// --- Wix ---

/** A page in the merchant's Wix dashboard. `path` is relative to the site's dashboard root. */
export function wixDashboardUrl(siteId: string, path = ''): string {
  const base = `https://manage.wix.com/dashboard/${siteId}`;
  return path ? `${base}/${path}` : base;
}

/** The SparkLayer app's page inside the merchant's Wix dashboard. */
export function wixAppUrl(siteId: string): string {
  return wixDashboardUrl(siteId, `app/${getWixAppId()}`);
}

// --- BigCommerce ---

/** The merchant's BigCommerce storefront root. */
export function bigcommerceStorefrontUrl(storeHash: string): string {
  return `https://store-${storeHash}.mybigcommerce.com`;
}

/** A page in the merchant's BigCommerce admin. `path` is relative to `/manage`. */
export function bigcommerceAdminUrl(storeHash: string, path = ''): string {
  const base = `${bigcommerceStorefrontUrl(storeHash)}/manage`;
  return path ? `${base}/${path}` : base;
}

// --- Cross-platform ---
//
// Concepts that exist on more than one platform. Each returns null when the platform has no known
// equivalent, so callers render a plain label instead of a broken link.

/** The store's public storefront root. */
export function platformStorefrontUrl(platform?: Platform, storeId?: MaybeId): string | null {
  if (!storeId) return null;
  if (isShopify(platform)) return `https://${storeId}`;
  if (platform === 'bigcommerce') return bigcommerceStorefrontUrl(storeId);
  return null;
}

/** An order's page in the platform's own admin. `orderId` is the platform's ID, not SparkLayer's. */
export function platformOrderUrl(
  platform?: Platform,
  storeId?: MaybeId,
  orderId?: MaybeId,
): string | null {
  if (!storeId || !orderId) return null;
  if (isShopify(platform)) return shopifyAdminUrl(storeId, `orders/${orderId}`);
  if (platform === 'wix') return wixDashboardUrl(storeId, `ecom-platform/order-details/${orderId}`);
  if (platform === 'bigcommerce') return bigcommerceAdminUrl(storeId, `orders/${orderId}`);
  return null;
}

/** A customer's page in the platform's own admin. */
export function platformCustomerUrl(
  platform?: Platform,
  storeId?: MaybeId,
  customerId?: MaybeId,
): string | null {
  if (!storeId || !customerId) return null;
  if (isShopify(platform)) return shopifyAdminUrl(storeId, `customers/${customerId}`);
  return null;
}

/**
 * The platform's own customer list — where customers are tagged into SparkLayer groups.
 * Null when the platform has no known one, so callers can fall back to our own /customers.
 */
export function platformCustomersUrl(platform?: Platform, storeId?: MaybeId): string | null {
  if (!storeId) return null;
  if (isShopify(platform)) return shopifyAdminUrl(storeId, 'customers');
  if (platform === 'wix') return wixDashboardUrl(storeId, 'contacts');
  return null;
}

/** A product's page in the platform's own admin. */
export function platformProductUrl(
  platform?: Platform,
  storeId?: MaybeId,
  productId?: MaybeId,
): string | null {
  if (!storeId || !productId) return null;
  if (isShopify(platform)) return shopifyAdminUrl(storeId, `products/${productId}`);
  return null;
}

/** A product variant's page in the platform's own admin. */
export function platformVariantUrl(
  platform?: Platform,
  storeId?: MaybeId,
  productId?: MaybeId,
  variantId?: MaybeId,
): string | null {
  if (!storeId || !productId || !variantId) return null;
  if (isShopify(platform)) {
    return shopifyAdminUrl(storeId, `products/${productId}/variants/${variantId}`);
  }
  return null;
}
